import AdminTable from '../../../components/admin/AdminTable';
﻿// pages/ConfigHorasRegaloPage.tsx
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import { fetchHorasRegaloConfigs, HorasRegaloConfig } from '../../../services/horasRegaloConfig';
import AddConfigrHoras from '../../../components/config-horas-regalo/AddConfigHoras';
import EditConfigHoras from '../../../components/config-horas-regalo/EditConfigHoras';
import DeleteConfigHoras from '../../../components/config-horas-regalo/DeleteConfigHoras';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const ConfigHorasRegaloPage: React.FC = () => {
    const [configs, setConfigs] = useState<HorasRegaloConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<HorasRegaloConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('horasregaloconfig', userLevelId);

    const loadConfigs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (perm?.can_read) {
                const data = await fetchHorasRegaloConfigs();
                setConfigs(data);
            } else {
                setConfigs([]);
            }
        } catch (err) {
            setError('Error al cargar las configuraciones');
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm !== null) {
            loadConfigs();
        }
    }, [loadConfigs, perm]);

    const handleRefresh = () => {
        loadConfigs();
    };

    const handleConfigCreated = (newConfig: HorasRegaloConfig) => {
        setConfigs(prev => [...prev, newConfig]);
    };

    const handleConfigUpdated = (updatedConfig: HorasRegaloConfig) => {
        setConfigs(prev => prev.map(config => 
            config.id === updatedConfig.id ? updatedConfig : config
        ));
    };

    const handleConfigDeleted = (deletedConfigId: number) => {
        setConfigs(prev => prev.filter(config => config.id !== deletedConfigId));
    };

    const openEditModal = (config: HorasRegaloConfig) => {
        setSelectedConfig(config);
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (config: HorasRegaloConfig) => {
        setSelectedConfig(config);
        setIsDeleteModalOpen(true);
    };

    const getPlanServicioResumido = (config: HorasRegaloConfig) => {
        if (config.precio_plan_servicio_info) {
            return config.precio_plan_servicio_info;
        }
        return `${config.servicio_asociado_nombre} - ${config.plan_config_info}`;
    };

    const getPreciosPermitidosResumido = (config: HorasRegaloConfig) => {
        if (config.precios_plan_permitidos_info && config.precios_plan_permitidos_info.length > 0) {
            return config.precios_plan_permitidos_info
                .map(pps => pps.plan_servicio)
                .slice(0, 2)
                .join(', ') + (config.precios_plan_permitidos_info.length > 2 ? '...' : '');
        }
        return 'Todos los precios';
    };

    const sortedConfigs = [...configs].sort((a, b) => a.horas_asignadas - b.horas_asignadas);
    const totalPages = Math.ceil(sortedConfigs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = sortedConfigs.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando horas de regalo..." />;

    if (error) {
        return (
            <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
                <div className="flex justify-center items-center h-64">
                    <div className="text-lg text-red-600">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Configuración de Horas de Regalo</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/config-horas-regalo" className="text-[#c1348b] hover:text-[#8f1f64]">Configuración de Horas de Regalo</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                        title="Refrescar lista"
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        <History className="w-5 h-5" />
                    </button>
                    {perm.can_create && (
                        <button
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                            onClick={() => setIsModalOpen(true)}
                            disabled={loading}
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Configuración
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {isModalOpen && (
                    <AddConfigrHoras
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onCreated={handleConfigCreated}
                    />
                )}

                {isEditModalOpen && selectedConfig && (
                    <EditConfigHoras
                        isOpen={isEditModalOpen}
                        onClose={() => setIsEditModalOpen(false)}
                        onUpdated={handleConfigUpdated}
                        config={selectedConfig}
                    />
                )}

                {isDeleteModalOpen && selectedConfig && (
                    <DeleteConfigHoras
                        isOpen={isDeleteModalOpen}
                        onClose={() => setIsDeleteModalOpen(false)}
                        onDeleted={handleConfigDeleted}
                        config={selectedConfig}
                    />
                )}

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Plan - Servicio Principal
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Horas Asignadas
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Precios Permitidos
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Fecha Creación
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Estado
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando configuraciones...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : displayedConfigs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay configuraciones disponibles
                                            </span>
                                            {perm.can_create && (
                                                <p className="text-gray-500 text-sm">
                                                    Agrega una nueva configuración para comenzar
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                displayedConfigs.map((config) => (
                                    <tr key={config.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                                            {getPlanServicioResumido(config)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {config.horas_asignadas} horas
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500" title={getPreciosPermitidosResumido(config)}>
                                            {getPreciosPermitidosResumido(config)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-500">
                                            {new Date(config.fecha_creacion).toLocaleDateString('es-ES', {
                                                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                            })}
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className={`px-2 inline-flex text-xs font-medium rounded-full ${config.activo
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                }`}>
                                                {config.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex gap-2">
                                                {perm.can_update && (
                                                    <button
                                                        className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                        title="Editar configuración"
                                                        onClick={() => openEditModal(config)}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {perm.can_delete && (
                                                    <button
                                                        className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                        title="Eliminar configuración"
                                                        onClick={() => openDeleteModal(config)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {perm.can_read && !loading && sortedConfigs.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={sortedConfigs.length}
                    />
                )}
            </div>
        </div>
    );
};

export default ConfigHorasRegaloPage;
